import { Box } from "@mui/material";
import { cloneDeep } from "lodash";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ORDERWISE_PAYOUT_COLUMNS_VISIBILITY,
  RECON_ORDER_TYPES,
} from "../../../data/accounting-constants";
import { AGGREGATOR_TYPE } from "../../../data/outlet-status-constants";
import {
  getFulfillmentStatusTextColor,
  transformReconAggregationData,
} from "../../../helpers/accounting-helper";
import { currencyAmountFormat } from "../../../helpers/text-format-helper";
import { RootState } from "../../../redux/store";
import "./style.scss";
import {
  DataGrid,
  GridColumns,
  GridOverlay,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExport,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { convertMomentToString } from "../../../helpers/moment-helper";
import { DATE_TIME_FORMATS } from "../../../data/constants";
import no_data_found from "../../../assets/images/no-data-found.png";

interface Props {
  aggregatorType: string;
  onBackCtaClick: any;
}

const ReconReportsView: FC<Props> = (props: Props) => {
  const { onBackCtaClick, aggregatorType } = props;

  const [selectedOrderTypeId, setSelectedOrderTypeId] = useState<any>(
    RECON_ORDER_TYPES[0].type
  );
  const [reconAggregationDetails, setReconAggregationDetails] = useState<any>(
    {}
  );
  const { reconAggregationData, orderwisePayoutData } = useSelector(
    (state: RootState) => ({
      reconAggregationData: state.accountingReducer.reconAggregationData,
      orderwisePayoutData: state.accountingReducer.orderwisePayoutData,
    })
  );

  const orderwisePayoutDataCols: GridColumns<any> = [
    {
      field: "ORDER_DATE",
      headerName: "Order Date",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<any>) =>
        convertMomentToString(params.value, DATE_TIME_FORMATS.dayMonthYear),
    },
    {
      field: "ORDER_ID",
      headerName: "Order Id",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ORDER_NOT_FOUND_ON",
      headerName: "Not Found on",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      cellClassName: "capitalize",
      valueFormatter: (params: GridValueFormatterParams<any>) =>
        params.value || "-",
    },
    {
      field: "FULFILLMENT_STATUS",
      headerName: "Fulfilment Status",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: renderFullfillmentStatusCell,
    },
    {
      field: "CUSTOMER_PAYABLE_RISTA",
      headerName: "Total Customer Payable (Rista)",
      width: 250,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "CUSTOMER_PAYABLE_AGGREGATOR",
      headerName: "Total Customer Payable (Aggregator)",
      width: 280,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "AGGREGATOR_PLATFORM_FEE_RISTA",
      headerName: "Aggregator Platform Fee (Rista)",
      width: 250,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "AGGREGATOR_PLATFORM_FEE_AGGREGATOR",
      headerName: "Aggregator Platform Fee (Aggregator)",
      width: 280,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "FINAL_PAYOUT_RISTA",
      headerName: "Final Payout (Rista)",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "FINAL_PAYOUT_AGGREGATOR",
      headerName: "Final Payout (Aggregator)",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: formatAmountValue,
    },
    {
      field: "difference",
      headerName: "Difference",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      cellClassName: "p-0",
      valueGetter: calculateDifferenceValue,
      renderCell: renderDifferenceCell,
    },
  ];

  useEffect(() => {
    if (reconAggregationData?.length) getTransformedAggregationData();
  }, [reconAggregationData]);

  function formatAmountValue(params: GridValueFormatterParams<any>) {
    return currencyAmountFormat(params.value, 2) || "-";
  }

  function calculateDifferenceValue(params: GridValueGetterParams<any, any>) {
    let systemValue = params?.row?.FINAL_PAYOUT_RISTA,
      aggregatorValue = params?.row?.FINAL_PAYOUT_AGGREGATOR,
      diff: any = null;
    if (systemValue !== null && aggregatorValue !== null) {
      diff = Number(aggregatorValue) - Number(systemValue);
    }
    return diff;
  }

  function getTransformedAggregationData() {
    let reconAggregationCloneData = cloneDeep(reconAggregationData || []);
    let dataToUpdate = reconAggregationCloneData.reduce(
      (prevVal: any, curVal: any) => {
        let transformedData = transformReconAggregationData(curVal);
        return {
          ...prevVal,
          [transformedData.orderType]: transformedData.data,
        };
      },
      {}
    );
    setReconAggregationDetails(dataToUpdate);
  }

  function renderDifferenceCell(params: GridRenderCellParams<any, any, any>) {
    let diff = params.value;
    return (
      <div
        className={`w-full h-full flex items-center justify-center ${
          diff && diff > 0
            ? "bg-[#EFFFEF] text-[#4E875B]"
            : diff && diff < 0
            ? "bg-[#FEE1E1] text-[#AB3737]"
            : "text-[#404040]"
        }`}
      >
        {diff ? `${diff > 0 ? "+" : ""}${currencyAmountFormat(diff, 2)}` : "-"}
      </div>
    );
  }

  function renderFullfillmentStatusCell(
    params: GridRenderCellParams<any, any, any>
  ) {
    let colorValue = getFulfillmentStatusTextColor(params?.value);
    return params?.value ? (
      <div className="flex items-center">
        <div
          className="w-8 h-8 rounded-[50%] mr-8"
          style={{ backgroundColor: colorValue }}
        ></div>
        <p className="font-semibold" style={{ color: colorValue }}>
          {params.value}
        </p>
      </div>
    ) : (
      <p className="">-</p>
    );
  }

  function renderReconReportsHeader() {
    return (
      <div className="flex items-center">
        <div
          className="flex items-center cursor-pointer"
          onClick={onBackCtaClick}
        >
          <div className="rista-sprite ic-arrow-left-blue"></div>
          <p className="text-16 text-[#2A77F2] font-medium ml-6">Back</p>
        </div>
        <div className="text-20 font-medium flex items-center h-34 mx-24 pl-30 border-l border-[#DEDEDF]">
          Reconciliation report
        </div>
        {RECON_ORDER_TYPES.map((orderType: any) => {
          return (
            <div
              key={orderType.type}
              className={`flex justify-center items-center py-8 text-14 rounded-[6px]
                mr-12 cursor-pointer w-[162px] ${
                  orderType.type === selectedOrderTypeId
                    ? "text-[#2A77F2] font-semibold bg-[#EAF1FE]"
                    : "text-[#404040] font-medium border border-[#EBEBEB]"
                }
              `}
              onClick={() => setSelectedOrderTypeId(orderType.type)}
            >
              {orderType.text}
            </div>
          );
        })}
      </div>
    );
  }

  function renderAggregationTableRow(dataObj: any) {
    let isCount = dataObj.key === "totalRecords",
      systemValue = dataObj?.rista,
      aggregatorValue = dataObj?.aggregator,
      diff = null;
    if (dataObj?.rista !== null && dataObj?.aggregator !== null) {
      diff = Number(aggregatorValue) - Number(systemValue);
    }
    return (
      <div className="w-full flex border border-t-0 border-[#DEDEDF]">
        <div className="text-15 text-[#404040] font-semibold flex items-center grow basis-0 py-14 px-28 border-r border-[#DEDEDF]">
          {dataObj?.label}
        </div>
        <div className="text-14 text-[#404040] flex items-center grow basis-0 py-14 px-28 border-r border-[#DEDEDF] text-center">
          {(isCount ? systemValue : currencyAmountFormat(systemValue, 2)) ||
            "-"}
        </div>
        <div
          className={`text-14 text-[#404040] flex items-center grow basis-0 py-14 px-28 text-center ${
            diff && diff > 0 ? "font-semibold" : ""
          }`}
        >
          {(isCount
            ? aggregatorValue
            : currencyAmountFormat(aggregatorValue, 2)) || "-"}
        </div>
        {selectedOrderTypeId === RECON_ORDER_TYPES[1].type ? (
          <div
            className={`text-14 grow flex items-center basis-0 py-14 px-28 border-l border-[#DEDEDF] text-center
          ${
            diff
              ? diff > 0
                ? "bg-[#EFFFEF] text-[#4E875B]"
                : "bg-[#FEE1E1] text-[#AB3737]"
              : "text-[#404040]"
          }
        `}
          >
            {diff === null
              ? "-"
              : `${diff > 0 ? "+" : ""}${
                  isCount ? diff : currencyAmountFormat(diff, 2)
                }`}
          </div>
        ) : null}
      </div>
    );
  }

  function renderAggregationDataTable() {
    return (
      <Box className="w-full mt-20">
        <div className="bg-[#EDEDED] w-full flex border-b border-[#DEDEDF]">
          <div className="text-14 font-semibold flex items-center grow basis-0 py-14 px-28 border-r border-[#DEDEDF]">
            #
          </div>
          <div className="text-14 font-semibold flex items-center grow basis-0 py-14 px-28 border-r border-[#DEDEDF] text-center">
            System Report
          </div>
          <div className="text-14 font-semibold flex items-center grow basis-0 py-14 px-28 text-center">
            {AGGREGATOR_TYPE[aggregatorType]?.label}
          </div>
          {selectedOrderTypeId === RECON_ORDER_TYPES[1].type ? (
            <div className="text-14 font-semibold flex items-center grow basis-0 py-14 px-28 border-l border-[#DEDEDF] text-center">
              Difference
            </div>
          ) : null}
        </div>
        {reconAggregationDetails[selectedOrderTypeId]?.map((dataObj: any) =>
          renderAggregationTableRow(dataObj)
        )}
      </Box>
    );
  }

  function renderCustomToolbar() {
    return (
      <GridToolbarContainer className="flex items-center justify-between py-12 px-16">
        <Box className="flex items-center">
          <p className="text-20 font-bold">Order-wise Payout</p>
          <p className="text-14 text-[#9F9F9F] ml-12">
            This is your order wise payout history
          </p>
        </Box>
        <GridToolbarExport className="text-14 text-[#4E875B] font-semibold px-16 py-8 bg-[#EFFFEF] rounded-[7px]" />
      </GridToolbarContainer>
    );
  }

  function renderCustomNoRowsOverlay() {
    return <GridOverlay>No data available</GridOverlay>;
  }

  function renderOrderwisePayoutData() {
    return (
      <Box sx={{ height: "60vh", width: "100%" }} className="mt-24">
        <DataGrid
          rows={orderwisePayoutData?.[selectedOrderTypeId] || []}
          columns={orderwisePayoutDataCols}
          disableColumnMenu
          disableSelectionOnClick
          getRowId={(row: any) => row?.ORDER_ID}
          showColumnRightBorder
          showCellRightBorder
          components={{
            Toolbar: renderCustomToolbar,
            NoRowsOverlay: renderCustomNoRowsOverlay,
          }}
          columnVisibilityModel={
            ORDERWISE_PAYOUT_COLUMNS_VISIBILITY[selectedOrderTypeId] || {}
          }
          className="recon-orderwise-payout-data"
        />
      </Box>
    );
  }

  function renderEmptyScreen() {
    return (
      <Box className="flex flex-col items-center justify-center h-[60vh]">
        <img src={no_data_found} alt="" />
        <p className="text-20 text-[#8DA3BE] font-semibold mb-10 mt-32">
          No Matched Orders Found
        </p>
        <p className="text-13 text-[#8DA3BE]">
          There are no orders in this tab. try to look into another tabs
        </p>
      </Box>
    );
  }

  return (
    <div className={`recon-reports-container m-20 mb-0 p-32 bg-white`}>
      {renderReconReportsHeader()}
      {reconAggregationDetails[selectedOrderTypeId] ||
      orderwisePayoutData?.[selectedOrderTypeId]?.length ? (
        <>
          {selectedOrderTypeId === RECON_ORDER_TYPES[2].type
            ? null
            : renderAggregationDataTable()}
          {selectedOrderTypeId === RECON_ORDER_TYPES[0].type
            ? null
            : renderOrderwisePayoutData()}
        </>
      ) : (
        renderEmptyScreen()
      )}
    </div>
  );
};

export default ReconReportsView;
